import {ReactNode, useEffect, useLayoutEffect, useReducer, useRef} from 'react';

import type {Observability} from '@stripe-internal/conversations-client';

import {AppCloseHandler, AppConfig, AppProfile} from '../../core/types';
import {ClientFetch, defaultClientFetch} from '../../core/lib/util/clientFetch';
import {createThreadsApp} from './createThreadsApp';
import {ParentMessageBus} from '../../core/lib/parentMessageBus/ParentMessageBus';
import {ThreadsAppContext} from './ThreadsAppContext';

type Props = {
  config: AppConfig;
  profile: AppProfile;
  clientFetch?: ClientFetch;
  parentMessageBus?: ParentMessageBus;
  observability?: Partial<Observability>;
  children: ReactNode;
  onClose?: AppCloseHandler;
  withHistory?: boolean;
};

type StableThreadsAppProps = Omit<Props, 'children' | 'clientFetch'> & {
  clientFetch: ClientFetch;
};

function useStableThreadsApp({
  config,
  profile,
  clientFetch,
  parentMessageBus,
  observability,
  onClose,
  withHistory = true,
}: StableThreadsAppProps): ReturnType<typeof createThreadsApp> | null {
  const appRef = useRef<ReturnType<typeof createThreadsApp> | null>(null);
  const [, forceRender] = useReducer((count) => count + 1, 0);
  const lastPropsRef = useRef({
    config,
    profile,
    clientFetch,
    parentMessageBus,
    onClose,
    observability,
    withHistory,
  });

  useLayoutEffect(() => {
    if (appRef.current) {
      return;
    }

    appRef.current = createThreadsApp(
      lastPropsRef.current.config,
      lastPropsRef.current.profile,
      lastPropsRef.current.clientFetch,
      lastPropsRef.current.parentMessageBus,
      lastPropsRef.current.onClose,
      lastPropsRef.current.observability,
      lastPropsRef.current.withHistory,
    );
    forceRender();
  }, []);

  const app = appRef.current;

  useEffect(() => {
    if (!app) {
      return;
    }

    app.init();
    return () => {
      app.destroy();
    };
  }, [app]);

  useEffect(() => {
    if (!app || Object.is(config, lastPropsRef.current.config)) {
      return;
    }

    app.updateConfig(config);
    lastPropsRef.current.config = config;
  }, [app, config]);

  useEffect(() => {
    if (!app || Object.is(profile, lastPropsRef.current.profile)) {
      return;
    }

    // Keep SDK history behavior consistent with the runtime selected at mount.
    app.updateProfile({
      ...profile,
      featureFlags: {
        ...profile.featureFlags,
        enableConversationsThreadsHistory: app.state.withHistory,
      },
    });
    lastPropsRef.current.profile = profile;
  }, [app, profile]);

  useEffect(() => {
    if (!app || Object.is(onClose, lastPropsRef.current.onClose)) {
      return;
    }

    app.updateOnClose(onClose);
    lastPropsRef.current.onClose = onClose;
  }, [app, onClose]);

  useEffect(() => {
    if (
      !app ||
      Object.is(parentMessageBus, lastPropsRef.current.parentMessageBus)
    ) {
      return;
    }

    app.updateParentMessageBus(parentMessageBus);
    lastPropsRef.current.parentMessageBus = parentMessageBus;
  }, [app, parentMessageBus]);

  return app;
}

export function ThreadsAppProvider({
  config,
  profile,
  clientFetch = defaultClientFetch(),
  parentMessageBus,
  observability,
  onClose,
  withHistory,
  children,
}: Props) {
  const app = useStableThreadsApp({
    config,
    profile,
    clientFetch,
    parentMessageBus,
    observability,
    onClose,
    withHistory,
  });

  if (!app) {
    return null;
  }

  return (
    <ThreadsAppContext.Provider value={app}>
      {children}
    </ThreadsAppContext.Provider>
  );
}
